/**
 * useSwitchRole — calls the switch-role Edge Function, refreshes the
 * auth session so the new app_metadata.role flows into the JWT, and
 * clears the React Query cache so role-gated views re-fetch under the
 * new active hat.
 *
 * Mirrors useSwitchOrganization. On success we navigate to
 * /app/dashboard rather than reloading the current URL — a task or
 * admin URL valid for the prior hat may be invisible under the new
 * one (e.g. supervisor on /app/tasks/<id> → cleaner can't SELECT
 * that task under cleaning_tasks_select RLS, surfacing as the
 * generic "Failed to load task" wedge on 2026-05-18). Going through
 * /app/dashboard lets DashboardRouter pick the correct surface for
 * the active hat. window.location.assign() still triggers a full
 * load, so the JWT-refresh + cache-clear safety net is unchanged.
 * Tests can opt out via { skipReload: true }.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'
import { PATHS }                                                from '@/router/paths'

export interface SwitchRoleVars {
  targetRole:  string
  skipReload?: boolean
}

export interface SwitchRoleResult {
  ok:            boolean
  previous_role: string | null
  new_role:      string
  org_id:        string
  roles:         string[]
  no_op?:        boolean
}

export function useSwitchRole(): UseMutationResult<SwitchRoleResult, AppError, SwitchRoleVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<SwitchRoleResult, AppError, SwitchRoleVars>({
    mutationFn: async (vars): Promise<SwitchRoleResult> => {
      const { data, error } = await supabase.functions.invoke<SwitchRoleResult>('switch-role', {
        body: { target_role: vars.targetRole },
      })
      if (error) {
        const ctx  = error as unknown as { context?: Response }
        let code   = 'SWITCH_ROLE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }

      const { error: refreshErr } = await supabase.auth.refreshSession()
      if (refreshErr) {
        throw new AppError({
          code:        'SESSION_REFRESH_FAILED',
          message:     refreshErr.message,
          userMessage: 'Switched on the server but the local session did not refresh. Sign out and back in.',
          status:      500,
          cause:       refreshErr,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      queryClient.clear()
      if (!vars.skipReload && typeof window !== 'undefined') {
        window.location.assign(PATHS.dashboard)
      }
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_TARGET_ROLE':   return 'That role name is not recognized.'
    case 'FORBIDDEN_TARGET_ROLE': return 'You do not hold that role in this organization.'
    case 'NO_ACTIVE_ORG':         return 'No active organization in your session. Sign out and back in.'
    case 'LOOKUP_FAILED':         return 'Could not verify your roles. Try again.'
    case 'UPDATE_FAILED':         return 'Could not switch roles. Try again.'
    case 'AUDIT_FAILED':          return 'Switch was rolled back due to an audit-trail error. Try again.'
    case 'RATE_LIMITED':          return 'Too many role switches in a row. Pause for a moment.'
    default:                      return detail || 'Could not switch roles.'
  }
}
